import { inject, Injectable } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map } from 'rxjs';

import { Asset, ColorInfoDataModel } from '@ppg/core/models';
import { FeatureFlagService, GlobalSettings } from '@ppg/shared/feature-flag';

import { MediaAsset, Product } from '../models';

@Injectable({ providedIn: 'root' })
export class AssetsProxyService {
  readonly #featureFlagService = inject(FeatureFlagService);

  readonly #urlReplacer = toObservable(this.#featureFlagService.globalSettings).pipe(
    filter(Boolean),
    map((settings) => this.createUrlReplacer(settings)),
  );

  replaceProductsAssetsUrls(products: Product[]) {
    return this.#urlReplacer.pipe(
      map((urlReplacer) => {
        if (!urlReplacer || !products?.length) {
          return products;
        }

        const processAssets = (assets: MediaAsset[] | undefined): MediaAsset[] | undefined =>
          assets?.map((asset) => ({
            ...asset,
            Url: urlReplacer(asset.Url),
          }));

        return products.map((product) => ({
          ...product,
          Assets: processAssets(product.Assets),
          Items: product.Items?.map((item) => ({
            ...item,
            Assets: processAssets(item.Assets),
          })),
        }));
      }),
    );
  }

  replaceColorsAssetsUrls(colors: ColorInfoDataModel[]) {
    return this.#urlReplacer.pipe(
      map((urlReplacer) => {
        if (!urlReplacer || !colors?.length) {
          return colors;
        }

        const processAssets = (assets: Asset[]): Asset[] =>
          assets.map((asset) => ({
            ...asset,
            url: urlReplacer(asset.url)!,
          }));

        return colors.map((color) => ({
          ...color,
          assets: processAssets(color.assets),
        }));
      }),
    );
  }

  private createUrlReplacer(globalSettings: GlobalSettings | undefined) {
    if (!globalSettings?.sharedSettings.assetsProxy) {
      return null;
    }

    const { enabled, source, destination } = globalSettings.sharedSettings.assetsProxy;
    if (!enabled || !source || !destination) {
      return null;
    }

    return (url: string | null) => (url ? url.replace(new RegExp(source, 'i'), destination) : url);
  }
}
